// Public pages that crawlers should index, and how their localized URLs look.
//
// The app keeps i18n `strategy: "no_prefix"` (locale from cookie) for signed-in
// users. For SEO, the public pages below also exist under a locale prefix
// (`/vi`, `/ja/downloader`, ...) so each language has its own indexable URL.
// English stays unprefixed and doubles as `x-default`.
import { DEFAULT_LOCALE, LOCALE_CODES, LOCALES } from "./locales";

// Locales that get a URL prefix (everything except the default).
export const SEO_LOCALES = LOCALE_CODES.filter((code) => code !== DEFAULT_LOCALE);

// Pages served in every locale (prefixed aliases + hreflang).
export const SEO_LOCALIZED_PATHS = [
  "/",
  "/downloader",
  "/video-to-subtitle",
  "/hardsub-to-subtitle",
  "/subtitle-translator",
  "/playlist-downloader",
  "/video-audio-to-summary",
  "/multi-downloader",
  "/youtube-tools",
  "/subtitle-summary",
  "/region-checker",
  "/extension",
];

// Public pages indexed in English only.
export const SEO_ENGLISH_ONLY_PATHS = ["/api", "/tools", "/about", "/contact", "/privacy", "/terms"];

export const SEO_PUBLIC_PATHS = [...SEO_LOCALIZED_PATHS, ...SEO_ENGLISH_ONLY_PATHS];

// vue-router param that only matches a known locale, so `/anything-else`
// still falls through to the 404 handler.
export const SEO_LANG_PARAM = `:lang(${SEO_LOCALES.join("|")})`;

// Route meta flag set on the localized aliases.
export const SEO_LANG_META = "seoLang";

// `/downloader` + `vi` -> `/vi/downloader`; `/` + `vi` -> `/vi`.
export const seoPath = (path: string, lang?: string | null): string => {
  if (!lang || lang === DEFAULT_LOCALE) return path;
  return `/${lang}${path === "/" ? "" : path}`;
};

// `/vi/downloader` -> `/downloader`; `/vi` -> `/`; `/downloader` -> `/downloader`.
export const stripSeoLang = (path: string): string => {
  const match = path.match(/^\/([a-z]{2,3})(?=\/|$)/);
  if (!match || !SEO_LOCALES.includes(match[1])) return path;
  return path.slice(match[0].length) || "/";
};

export const isSeoLocalizedPath = (path: string): boolean => SEO_LOCALIZED_PATHS.includes(stripSeoLang(path));

// hreflang / og:locale values. Google wants `zh-Hans` rather than bare `zh`.
export const hreflangCode = (code: string): string => (code === "zh" ? "zh-Hans" : code);

export const ogLocale = (code: string): string =>
  (LOCALES.find((l) => l.code === code)?.language || "en-US").replace("-", "_");
